import React, { useState, useEffect, useRef } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import Divider from '@mui/material/Divider';
import BackendApiService from '../services/BackendApiService';
import { getAccessToken } from '../utils/accessTokenStore';
import { getUserEmail } from '../utils/utils';

type PipelineActionDialogProps = {
  open: boolean;
  onClose: () => void;
  action: any;
  request: any;
}

function PipelineActionDialog(props: PipelineActionDialogProps) {
  const [userParameters, setUserParameters] = useState<Record<string, string>>({});
  const [apiParameters, setApiParameters] = useState<Record<string, string[]>>({});
  const [loadingApiParams, setLoadingApiParams] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [warning, setWarning] = useState<string | null>(null);
  const [pipelineRunUrl, setPipelineRunUrl] = useState<string | null>(null);
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [pipelineStatus, setPipelineStatus] = useState<'queued' | 'in-progress' | 'running' | 'succeeded' | 'failed' | null>(null);
  
  const pollingInterval = useRef<NodeJS.Timeout | null>(null);
  const pollCount = useRef<number>(0);
  const accessToken = getAccessToken();

  const moduleData = props.request.modules?.find((m: any) => m.id === props.request.id)
    || props.request.modules?.[0]
    || {};

  // Name is resolved by the backend; request.resourceName may be the SSR number
  const resourceDisplayName = String(
    moduleData.displayResourceName || props.request.displayResourceName || moduleData.resourceName || ''
  ).trim();

  useEffect(() => {
    if (!pipelineId || !runId || (pipelineStatus !== 'queued' && pipelineStatus !== 'in-progress' && pipelineStatus !== 'running')) {
      return;
    }

    pollCount.current = 0;
    const MAX_POLL_ATTEMPTS = 30; // 30 * 10s = 5 minutes

    const pollStatus = async () => {
      try {
        pollCount.current += 1;

        // Check timeout
        if (pollCount.current > MAX_POLL_ATTEMPTS) {
          console.warn('Polling timeout reached');
          setPipelineStatus(null);
          setSuccess(null);
          setWarning('Status currently unavailable. The action may still be running. Please check the pipeline directly.');
          if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
          }
          return;
        }

        console.log('=== Polling Pipeline Status ===');
        console.log('Pipeline ID:', pipelineId);
        console.log('Run ID:', runId);
        console.log('Poll attempt:', pollCount.current, '/', MAX_POLL_ATTEMPTS);
        
        const backendApiService = new BackendApiService(accessToken ?? '');
        const statusResult = await backendApiService.getPipelineStatus(pipelineId, runId);
        const statusData = statusResult?.body || statusResult;

        console.log('Raw status result:', statusResult);
        console.log('Pipeline status data:', statusData);

        if (statusData.status === 'completed') {
          if (statusData.result === 'succeeded') {
            setPipelineStatus('succeeded');
            setSuccess(`Action completed successfully!`);
          } else {
            setPipelineStatus('failed');
            setError(`Action failed: ${statusData.result}`);
          }
          if (pollingInterval.current) {
            clearInterval(pollingInterval.current);
          }
        } else if (statusData.status === 'pending') {
          setPipelineStatus('queued');
          setSuccess(`Action queued. Waiting for available agent...`);
        } else if (statusData.status === 'inprogress') {
          setPipelineStatus('in-progress');
          setSuccess(`Action is running...`);
        }
      } catch (err: any) {
        console.error('Error polling pipeline status:', err);
      }
    };

    // Poll immediately, then every 10 seconds
    pollStatus();
    pollingInterval.current = setInterval(pollStatus, 10000);

    return () => {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
    };
  }, [pipelineId, runId, pipelineStatus, accessToken]);

  // Load azure-api parameters when dialog opens
  useEffect(() => {
    if (!props.open) return;

    const loadApiParameters = async () => {
      const apiParams = props.action.parameters?.filter((p: any) => p.source === 'azure-api') || [];
      
      for (const param of apiParams) {
        if (param.apiMethod === 'getSnapshots') {
          setLoadingApiParams(prev => ({ ...prev, [param.name]: true }));
          
          try {
            const backendApiService = new BackendApiService(accessToken ?? '');
            const region = moduleData.region || 'az';
            const moduleName = props.request.name;
            const platform = props.request.platform;
            
            const snapshots = await backendApiService.getAzureSnapshots(
              resourceDisplayName,
              region,
              moduleName,
              platform
            );
            
            setApiParameters(prev => ({ ...prev, [param.name]: snapshots }));
          } catch (err: any) {
            console.error(`Error loading ${param.name}:`, err);
            setError(`Failed to load ${param.label || param.name}: ${err.message}`);
          } finally {
            setLoadingApiParams(prev => ({ ...prev, [param.name]: false }));
          }
        }
      }
    };

    loadApiParameters();
  }, [props.open, props.action, props.request, accessToken, resourceDisplayName, moduleData.region]);

  const handleParameterChange = (paramName: string, value: string) => {
    setUserParameters(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  // Build complete parameter map for display
  const getAllParameters = () => {
    const allParams: Record<string, { value: string; label: string; source: string }> = {};
    
    // Add all configured parameters
    props.action.parameters?.forEach((param: any) => {
      let value = '';
      let source = param.source;
      
      switch (param.source) {
        case 'resourceName':
          value = resourceDisplayName;
          break;
        case 'platform':
          value = props.request.platform;
          break;
        case 'user-input':
        case 'azure-api':
        case 'parent':
          value = userParameters[param.name] || '';
          break;
      }
      
      allParams[param.name] = {
        value,
        label: param.label || param.name,
        source
      };
    });
    
    // Add requesterEmail (always included)
    const userEmail = accessToken ? getUserEmail(accessToken) : null;
    const displayValue = userEmail ? `Current user (${userEmail})` : 'Current user';
    
    allParams['requesterEmail'] = {
      value: displayValue,
      label: 'Requester Email',
      source: 'auto'
    };
    
    return allParams;
  };

  const handleTrigger = async () => {
    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      const backendApiService = new BackendApiService(accessToken ?? '');
      
      const result = await backendApiService.triggerPipelineAction(
        props.request.name,
        props.request.platform,
        props.action.name,
        props.request.pullRequestId,
        moduleData,
        userParameters
      );

      console.log('Pipeline trigger response:', result);

      const responseData = result?.body || result;

      if (responseData && responseData.success) {
        setPipelineRunUrl(responseData.runUrl || null);
        setRunId(responseData.runId);
        setPipelineId(props.action.pipelineId);
        setPipelineStatus('queued');
        setSuccess(`Action queued. Waiting for available agent...`);
      } else {
        setError(responseData?.message || responseData?.error || 'Failed to execute action');
      }
    } catch (err: any) {
      console.error('Pipeline trigger error:', err);
      setError(err.message || 'An error occurred while triggering the pipeline');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      if (pollingInterval.current) {
        clearInterval(pollingInterval.current);
      }
      setError(null);
      setSuccess(null);
      setWarning(null);
      setPipelineRunUrl(null);
      setPipelineId(null);
      setRunId(null);
      setPipelineStatus(null);
      setUserParameters({});
      pollCount.current = 0;
      props.onClose();
    }
  };

  // Get user-input and azure-api parameters
  const userInputParams = props.action.parameters?.filter(
    (p: any) => p.source === 'user-input'
  ) || [];
  
  const azureApiParams = props.action.parameters?.filter(
    (p: any) => p.source === 'azure-api'
  ) || [];

  const parentParams = props.action.parameters?.filter(
    (p: any) => p.source === 'parent'
  ) || [];

  // Initialize parent parameters
  useEffect(() => {
    if (!props.open || parentParams.length === 0) return;

    const parentValues: Record<string, string> = {};
    parentParams.forEach((param: any) => {
      const parentData = props.request.modules?.[0]?.parent;
      if (parentData) {
        const field = param.parentField || 'resource';
        parentValues[param.name] = parentData[field] || '';
      }
    });

    setUserParameters(prev => ({ ...prev, ...parentValues }));
  }, [props.open, props.action]);

  return (
    <Dialog 
      open={props.open} 
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        {props.action.name}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          This will trigger an action "{props.action.name}" for: <strong>{props.request.title}</strong>
        </DialogContentText>

        {(userInputParams.length > 0 || azureApiParams.length > 0) && (
          <Box sx={{ mt: 2 }}>
            {userInputParams.map((param: any) => (
              <TextField
                key={param.name}
                fullWidth
                margin="normal"
                label={param.label || param.name}
                value={userParameters[param.name] || param.defaultValue || ''}
                onChange={(e) => handleParameterChange(param.name, e.target.value)}
                required={param.required}
                helperText={param.required ? 'Required' : ''}
              />
            ))}
            
            {azureApiParams.map((param: any) => (
              <FormControl
                key={param.name}
                fullWidth
                margin="normal"
                required={param.required}
                disabled={loadingApiParams[param.name]}
              >
                <InputLabel>{param.label || param.name}</InputLabel>
                <Select
                  value={userParameters[param.name] || ''}
                  onChange={(e) => handleParameterChange(param.name, e.target.value)}
                  label={param.label || param.name}
                  IconComponent={loadingApiParams[param.name] ? () => (
                    <CircularProgress size={20} sx={{ mr: 2 }} />
                  ) : undefined}
                >
                  {loadingApiParams[param.name] ? (
                    <MenuItem disabled>Loading snapshots...</MenuItem>
                  ) : apiParameters[param.name]?.length > 0 ? (
                    apiParameters[param.name].map((option: string) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No snapshots available</MenuItem>
                  )}
                </Select>
                {param.required && <FormHelperText>Required</FormHelperText>}
              </FormControl>
            ))}

            {parentParams.map((param: any) => (
              <TextField
                key={param.name}
                margin="normal"
                label={param.label || param.name}
                fullWidth
                value={userParameters[param.name] || ''}
                disabled
                variant="filled"
                helperText="This value is automatically set"
              />
            ))}
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {warning && (
          <Alert 
            severity="warning" 
            sx={{ mt: 2 }}
            action={
              pipelineRunUrl && (
                <IconButton 
                  size="small"
                  href={pipelineRunUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit' }}
                  title="View Pipeline Run"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )
            }
          >
            {warning}
          </Alert>
        )}

        {success && (
          <Alert 
            severity={
              pipelineStatus === 'failed' ? 'error' : 
              pipelineStatus === 'succeeded' ? 'success' : 
              pipelineStatus === 'queued' ? 'warning' :
              'info'
            }
            sx={{ 
              mt: 2,
              ...(pipelineStatus === 'queued' && {
                backgroundColor: 'action.hover',
                color: 'text.secondary',
                '& .MuiAlert-icon': {
                  color: 'text.secondary'
                }
              })
            }}
            icon={
              (pipelineStatus === 'queued' || pipelineStatus === 'in-progress' || pipelineStatus === 'running') ? (
                <CircularProgress size={20} />
              ) : pipelineStatus === 'succeeded' ? (
                <CheckCircleIcon />
              ) : pipelineStatus === 'failed' ? (
                <CancelIcon />
              ) : undefined
            }
            action={
              pipelineRunUrl && (
                <IconButton 
                  size="small"
                  href={pipelineRunUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: 'inherit' }}
                  title="View Pipeline Run"
                >
                  <OpenInNewIcon fontSize="small" />
                </IconButton>
              )
            }
          >
            {success}
          </Alert>
        )}

        {/* Parameter Summary */}
        {!pipelineStatus && (
          <Paper 
            variant="outlined" 
            sx={{ 
              mt: 3, 
              p: 2,
              backgroundColor: 'background.default'
            }}
          >
            <Typography variant="subtitle2" gutterBottom color="text.secondary">
              Parameters to be sent:
            </Typography>
            <List dense disablePadding>
              {Object.entries(getAllParameters()).map(([key, param], index, array) => {
                const isEmpty = !param.value || param.value === '';
                const isRequired = props.action.parameters?.find((p: any) => p.name === key)?.required !== false;
                const showWarning = isEmpty && isRequired && key !== 'requesterEmail';
                
                return (
                  <React.Fragment key={key}>
                    <ListItem 
                      sx={{ 
                        px: 0, 
                        py: 0.5,
                        ...(showWarning && {
                          backgroundColor: 'warning.light',
                          borderRadius: 1,
                          px: 1
                        })
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography variant="body2">
                            <strong>{param.label}:</strong> {param.value || '(empty)'}
                          </Typography>
                        }
                        primaryTypographyProps={{
                          component: 'div'
                        }}
                      />
                    </ListItem>
                    {index < array.length - 1 && <Divider />}
                  </React.Fragment>
                );
              })}
            </List>
          </Paper>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          {success ? 'Close' : 'Cancel'}
        </Button>
        {!success && (
          <Button 
            onClick={handleTrigger} 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Executing...' : 'Execute'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default PipelineActionDialog;
